import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { bufferTime, filter, map, take, takeUntil } from 'rxjs/operators';
import { MisoProperties } from './miso-interfaces';
import { WebsocketService } from '~app/shared/websocket/websocket.service';
import { AuthService } from 'app/auth/auth.service';
import { MapService } from 'app/shared/map/map.service';
import { FeatureCollection, Point } from 'geojson';
import { Layer } from '~app/shared/map/layer/layer.model';
import { DefaultMap } from '~app/shared/utils';

@Injectable()
export class MeasurementsService implements OnDestroy {

  private readonly ngUnsubscribe = new Subject<void>();

  private defaultGeojson = new DefaultMap<string, FeatureCollection<Point>>(() => ({
    type: 'FeatureCollection',
    features: []
  }));

  constructor(
    private mapService: MapService,
    private readonly websocket: WebsocketService,
    private readonly authService: AuthService) {

    // Initialize after authentication
    authService.login$.pipe(take(1)).subscribe(login => {
      console.log(login);
      if (login.token) {
        this.init();
      }
    });
  }

  private async init() {
    console.log('Measurements service constructing');
    //// 1) join the configured rooms immediately for receiving measurements messages
    this.mapService.loadedLayers$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(layer => !!layer.metadata.roomName)
      )
      .subscribe((layer) => {
        this.sendJoin(layer)
      });

    //// 2) handle ws messages
    this.websocket.newWebsocketMessage$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(val => val.type === 'measureHeader'),
        map(val => this.enrich(val)),
        filter(val => !!val),
        bufferTime(2000, 200) //// 2 seconds or 200 points/measurements
      )
      .subscribe((freshPoints) => {
        for (const freshPoint of freshPoints) {
          const features = this.defaultGeojson.get(freshPoint.properties.eventType).features;
          const existingSlot = features.findIndex(pp => pp.id === freshPoint.id);
          if (existingSlot > -1) {
            features[existingSlot] = freshPoint;
          } else {
            features.push(freshPoint);
          }
        }
        this.pushToMap();
      });
  }

  ngOnDestroy() {
    //// layers are already loaded here..
    this.mapService.getLayerList()
      .filter(layer => layer.metadata?.roomName)
      .forEach(this.sendLeave);

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Join to room that is defined in the metedata of the layer.
   * @param layer
   */
  private sendJoin(layer: Layer) {
    console.log(`joining room: ${layer.metadata.roomName}`);
    this.websocket.sendPrimusMessage({
      action: 'join',
      room: `${this.authService.getTenant()}:${layer.metadata.roomName}:*`
    });
  }

  /**
   * Leave the room.
   * @param layer
   */
   private sendLeave(layer: Layer) {
    console.log(`leaving room: ${layer.metadata.roomName}`);
    this.websocket.sendPrimusMessage({
      action: 'leave',
      room: `${this.authService.getTenant()}:${layer.metadata.roomName}:*`
    });
  }

  /**
   * translate measurement message as geojson
   * @param msg
   */
  private enrich(msg: MisoProperties) {
    const existingMapItem = this.mapService.getFeatureById(msg?.body?.m?.deviceid.toUpperCase());
    //// more wisdom if mapItem does not exist?
    if (!existingMapItem) {
      return;
    }
    //// for example 'TUU_YVS1_SUUNTA_VIRTAUS'
    const measureDeviceId = `${msg?.body?.m?.deviceid}_${msg?.body?.m?.eventtype}`.toUpperCase();
    return {
      id: measureDeviceId,
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: (existingMapItem.geometry as Point).coordinates
      },
      //// if more properties are needed, then forward the payload 'as is'
      properties: {
        qnty: parseInt(msg?.body?.p?.qnty),
        deviceId: measureDeviceId,
        ts: new Date(Date.parse(msg?.body?.p?.ts)).toLocaleString(),
        eventType: msg?.body?.m?.eventtype
      }
    };
  }

  /**
   * push the point type geojson to map
   * @param geojson
   */
  private pushToMap() {
    this.mapService.getLayerList()
      .filter(layer => layer.metadata.roomName)
      .forEach(layer => {
        for (const [key, value] of this.defaultGeojson) {
          if (key === layer.metadata.roomName) {
            layer.pushGeojson(value);
          }
        }
      });
  }
}
